/**
 * Object-mode selection helpers.
 *
 * Object-mode selection is a `SelectionTarget[]` union — each entry is a
 * `sceneObject`, a `blendEntry`, or a `collection`. This module is the
 * single place in the codebase that understands how to:
 *
 *  - derive leaf scene-object ids from a target list (`selectionToLeafObjectIds`)
 *  - resolve a store key (mesh name / scene-object id) into its owner
 *    target (`resolveStoreKeySelectionTarget`) — the hit-to-owner
 *    promotion path used by the viewport
 *  - pick the single-active target for inspector-like UIs
 *    (`getSingleSelectionTarget`)
 *  - prune selection targets against the current scene graph after object,
 *    collection, or blend-entry deletion (`filterExistingSelectionTargets`)
 *
 * Consumers should never iterate the state's selection array directly
 * to derive leaves — go through these helpers so the ownership rules
 * (blend-entry monolithic, live-generator promotion) stay consistent.
 */

import type {
  BlendFileEntry,
  Collection,
  ObjectSelectionTarget,
} from "./storeTypes.ts";

export type { ObjectSelectionTarget } from "./storeTypes.ts";

import { findEntryByObjectName } from "./blendFileQueries.ts";
import { findCollectionIdForObject } from "./collectionGraph.ts";

export function objectSelectionTargetKey(
  target: ObjectSelectionTarget,
): string {
  return `${target.kind}:${target.id}`;
}

export function objectSelectionTargetsEqual(
  a: ObjectSelectionTarget,
  b: ObjectSelectionTarget,
): boolean {
  return a.kind === b.kind && a.id === b.id;
}

export function getSingleSelectionTarget(
  targets: readonly ObjectSelectionTarget[],
): ObjectSelectionTarget | null {
  return targets.length === 1 ? targets[0]! : null;
}

export function selectionTargetLeafIds(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  collections: ReadonlyMap<string, Collection>,
  target: ObjectSelectionTarget,
): string[] {
  switch (target.kind) {
    case "sceneObject":
      return [target.id];
    case "blendEntry": {
      const entry = blendFiles.get(target.id);
      return entry ? entry.file.objects.map((object) => object.name) : [];
    }
    case "collection": {
      const collection = collections.get(target.id);
      return collection ? [...collection.objectIds] : [];
    }
  }
}

/**
 * Flatten a list of selection targets into the set of leaf scene-object
 * ids they cover. A `sceneObject` contributes its own id; a `blendEntry`
 * contributes every object name in the entry; a `collection` contributes
 * every member id. Used by outline passes, hit proxies, and any
 * operation that needs to iterate the actual selected geometry.
 */
export function selectionToLeafObjectIds(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  collections: ReadonlyMap<string, Collection>,
  targets: readonly ObjectSelectionTarget[],
): Set<string> {
  const ids = new Set<string>();
  for (const target of targets) {
    for (const id of selectionTargetLeafIds(blendFiles, collections, target)) {
      ids.add(id);
    }
  }
  return ids;
}

export function filterExistingSelectionTargets(
  sceneObjects: ReadonlyMap<string, unknown>,
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  collections: ReadonlyMap<string, Collection>,
  targets: readonly ObjectSelectionTarget[],
): ObjectSelectionTarget[] {
  return targets.filter((target) => {
    switch (target.kind) {
      case "sceneObject":
        return sceneObjects.has(target.id);
      case "blendEntry":
        return blendFiles.has(target.id);
      case "collection":
        return collections.has(target.id);
    }
    return false;
  });
}

export function resolveStoreKeySelectionTarget(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  collections: ReadonlyMap<string, Collection>,
  key: string,
): ObjectSelectionTarget {
  const blendEntry = findEntryByObjectName(blendFiles, key);
  if (blendEntry) {
    return { kind: "blendEntry", id: blendEntry[0] };
  }
  const generatorCollectionId = findCollectionIdForObject(collections, key);
  if (generatorCollectionId) {
    const collection = collections.get(generatorCollectionId);
    if (collection?.generatorRef) {
      return { kind: "collection", id: generatorCollectionId };
    }
  }
  return { kind: "sceneObject", id: key };
}
